import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import { Field } from 'formik';
import {
  Card,
  Text,
  Box,
  Label,
  Radio,
  Heading,
  Flex,
  Link,
  Image,
} from '@resideo/blueprint-react';
import styled from 'styled-components';
import SeparatorLine from 'components/common/SeparatorLine';
import EditAddressModal from './EditAddressModal';
import CustomPopup from 'components/common/customPopup/customPopup.style';
import AddAddressModal from './AddAddressModal';
import ManageAddressModal from './ManageAddressModal';
import { useTranslation } from 'react-i18next';
import { CartContext } from 'routes/ServicesAndSubscriptions/CartProvider';
import { plusBlueIcon } from '../../../assets/images';

const Container = styled(Card)`
  border-radius: 0.9375rem;
  padding: 1.5625rem;
  border: 2px solid ${({ theme }) => theme.colors.lightenGray};
`;

export const StyledText = styled(Link)`
  cursor: pointer;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSizes.small};
  display: inline;
  margin-left: ${({ theme }) => theme.fontSizes.small};
`;

export const Address = ({ address, action }) => (
  <Flex alignItems='center'>
    <Text
      fontSize='medium'
      fontWeight='body'
      color='textLight3'
      fontFamily='Roboto'>
      {`${address.companyName}, `}
      {`${address.contactName}, `}
      {`${address.addressLine1}, `}
      {address.addressLine2 ? `${address.addressLine2}, ` : ''}
      {address.city} {address.stateProvinceRegionCode} {address.zipPostalCode}
      {action && action}
    </Text>
  </Flex>
);

const BillingOrShippingAddressForm: FC<any> = ({
  values,
  setFieldValue,
  setErrors,
  errors,
  forBilling = false,
  defaultBillTo = null,
  paymentSources,
  currentIndex,
  clickedAddressIndex,
  setClickedAddressIndex,
  currentPaymentIndexClicked,
  setCurrentPaymentIndexClicked,
  useDefault,
  setUseDefault,
  digitalRiverPayment,
}) => {
  const { state } = useContext(CartContext);
  const { t } = useTranslation();

  const allBilling = paymentSources
    ? paymentSources.map(obj => ({
        companyName: obj.billTo?.organization,
        contactName: `${obj.billTo?.firstName} ${obj.billTo?.lastName}`,
        addressLine1: obj.billTo?.address?.addressLine1,
        addressLine2: obj.billTo?.address?.addressLine2,
        countryCode: obj.billTo?.address?.countryCode,
        city: obj.billTo?.address?.city,
        stateProvinceRegionCode: obj.billTo?.address?.stateProvinceRegionCode,
        zipPostalCode: obj.billTo?.address.zipPostalCode,
        paymentSourceId: obj.paymentSourceId,
        clientSecret: obj.clientSecret,
      }))
    : [];

  const [defaultBillingAddress] = useState(
    defaultBillTo
      ? [
          {
            companyName: defaultBillTo?.organization,
            contactName: `${defaultBillTo?.firstName} ${defaultBillTo?.lastName}`,
            addressLine1: defaultBillTo?.address?.addressLine1,
            addressLine2: defaultBillTo?.address?.addressLine2,
            countryCode: defaultBillTo?.address?.countryCode,
            city: defaultBillTo?.address?.city,
            stateProvinceRegionCode:
              defaultBillTo?.address?.stateProvinceRegionCode,
            zipPostalCode: defaultBillTo?.address.zipPostalCode,
            isDefault: true,
          },
        ]
      : []
  ) as any;
  const [multipleAddresses, setMultipleAddresses] = useState(allBilling) as any;
  const [shippingOrBilling, setShippingOrBilling] = useState('billingAddress');

  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [countryCode, setCountry] = useState('');
  const [city, setCity] = useState('');
  const [stateProvinceRegionCode, setState] = useState('');
  const [zipPostalCode, setZipPostalCode] = useState('');

  const [openAddAddressModal, setOpenAddAddressModal] = useState(false);
  const closeAddAddressModal = () => setOpenAddAddressModal(false);

  const [openEditAddressModal, setOpenEditAddressModal] = useState(false);
  const closeEditAddressModal = () => setOpenEditAddressModal(false);

  const [openManageAddressModal, setOpenManageAddressModal] = useState(false);
  const closeManageAddressModal = () => setOpenManageAddressModal(false);

  // set other addresses to formik value
  values.multipleAddresses = multipleAddresses;

  const newAddress = {
    companyName,
    contactName,
    addressLine1,
    addressLine2,
    countryCode,
    city,
    stateProvinceRegionCode,
    zipPostalCode,
    isDefault: false,
  };

  const resetForm = () => {
    // clear form
    setCompanyName('');
    setContactName('');
    setAddressLine1('');
    setAddressLine2('');
    setCountry('');
    setCity('');
    setState('');
    setZipPostalCode('');

    // reset errors, close modal, end function
    setErrors({});
    closeAddAddressModal();
  };

  const useDefaultAddressForBilling = useCallback(
    a => {
      // set address to use
      setFieldValue('billingAddress.addressLine1', a.addressLine1);
      setFieldValue('billingAddress.addressLine2', a.addressLine2);
      setFieldValue('billingAddress.city', a.city);
      setFieldValue('billingAddress.companyName', a.companyName);
      setFieldValue('billingAddress.contactName', a.contactName);
      setFieldValue('billingAddress.countryCode', a.countryCode);
      setFieldValue('billingAddress.id', a.id);
      setFieldValue(
        'billingAddress.stateProvinceRegionCode',
        a.stateProvinceRegionCode
      );
      setFieldValue('billingAddress.zipPostalCode', a.zipPostalCode);
    },
    [setFieldValue]
  );

  const setShippingAddressFields = useCallback(
    a => {
      // set address to use
      setFieldValue(`${shippingOrBilling}.addressLine1`, a.addressLine1);
      setFieldValue(`${shippingOrBilling}.addressLine2`, a.addressLine2);
      setFieldValue(`${shippingOrBilling}.city`, a.city);
      setFieldValue(`${shippingOrBilling}.companyName`, a.companyName);
      setFieldValue(`${shippingOrBilling}.contactName`, a.contactName);
      setFieldValue(`${shippingOrBilling}.countryCode`, a.countryCode);
      setFieldValue(`${shippingOrBilling}.id`, a.id);
      setFieldValue(
        `${shippingOrBilling}.stateProvinceRegionCode`,
        a.stateProvinceRegionCode
      );
      setFieldValue(`${shippingOrBilling}.zipPostalCode`, a.zipPostalCode);
    },
    [setFieldValue]
  );

  const setShippingOrBillingNewCard = useCallback(
    a => {
      setFieldValue(`${shippingOrBilling}.addressLine1`, a.addressLine1);
      setFieldValue(`${shippingOrBilling}.addressLine2`, a.addressLine2);
      setFieldValue(`${shippingOrBilling}.city`, a.city);
      setFieldValue(`${shippingOrBilling}.companyName`, a.companyName);
      setFieldValue(`${shippingOrBilling}.countryCode`, a.countryCode);
      setFieldValue(`${shippingOrBilling}.id`, a.id);
      setFieldValue(
        `${shippingOrBilling}.stateProvinceRegionCode`,
        a.stateProvinceRegionCode
      );
      setFieldValue(`${shippingOrBilling}.zipPostalCode`, a.zipPostalCode);
    },
    [setFieldValue]
  );

  useEffect(() => {
    if ((paymentSources ?? []).length === 0) {
      setUseDefault(true);
      useDefaultAddressForBilling(defaultBillingAddress[0]);
    }
    if (!forBilling) {
      setShippingOrBilling('shippingAddress');
    }
    if (multipleAddresses && multipleAddresses.length === 1) {
      setShippingAddressFields(values.multipleAddresses[0]);
    }
  }, [
    multipleAddresses,
    values,
    setShippingAddressFields,
    forBilling,
    defaultBillTo,
  ]);

  const addNewAddress = () => {
    if (
      contactName === '' ||
      companyName === '' ||
      addressLine1 === '' ||
      city === '' ||
      zipPostalCode === '' ||
      countryCode === ''
    ) {
      setErrors({
        contactName: contactName === '' ? true : false,
        companyName: companyName === '' ? true : false,
        addressLine1: addressLine1 === '' ? true : false,
        city: city === '' ? true : false,
        zipPostalCode: zipPostalCode === '' ? true : false,
        countryCode: countryCode === '' ? true : false,
      });
    } else {
      // prevent duplicate address
      if (
        values.multipleAddresses.length > 0 &&
        values.multipleAddresses.find(
          a =>
            // if newly added address matches
            // any of the existing addresses
            newAddress.companyName === a.companyName &&
            newAddress.contactName === a.contactName &&
            newAddress.addressLine1 === a.addressLine1 &&
            newAddress.city === a.city &&
            newAddress.countryCode === a.countryCode &&
            newAddress.zipPostalCode === a.zipPostalCode &&
            newAddress.stateProvinceRegionCode === a.stateProvinceRegionCode
        )
      ) {
        resetForm();
        return;
      }

      // add new address
      setMultipleAddresses(multipleAddresses => [
        ...multipleAddresses,
        newAddress,
      ]);

      setCurrentPaymentIndexClicked(false);
      setClickedAddressIndex(multipleAddresses.length);

      if (values.paymentMethodRadio === 'existingSource') {
        updatePaymentSource(newAddress, currentIndex);
        if (multipleAddresses.length === 0) {
          setShippingAddressFields(newAddress);
        }
      } else {
        setShippingOrBillingNewCard(newAddress);
      }
      resetForm();
    }
  };

  const updateAddress = () => {
    values.multipleAddresses.filter((obj, i) => {
      if (i === clickedAddressIndex) {
        obj.addressLine1 = values[shippingOrBilling].addressLine1;
        obj.addressLine2 = values[shippingOrBilling].addressLine2;
        obj.city = values[shippingOrBilling].city;
        obj.companyName = values[shippingOrBilling].companyName;
        obj.contactName = values[shippingOrBilling].contactName;
        obj.countryCode = values[shippingOrBilling].countryCode;
        obj.stateProvinceRegionCode =
          values[shippingOrBilling].stateProvinceRegionCode;
        obj.zipPostalCode = values[shippingOrBilling].zipPostalCode;
      }
      return obj;
    });
    if (values.paymentMethodRadio === 'existingSource') {
      updatePaymentSource(multipleAddresses[clickedAddressIndex], currentIndex);
    }
    setErrors({});
    closeEditAddressModal();
  };

  const setDefaultAddress = a => {
    // set all defaults to false
    values.multipleAddresses.map(address => {
      if (address.isDefault) {
        address.isDefault = false;
      }
      return <></>;
    });

    // set correct address to default
    if (!a.isDefault) {
      Object.assign(a, { isDefault: true });
    }

    setMultipleAddresses([...multipleAddresses]);
  };

  const removeAddress = i =>
    setMultipleAddresses(values.multipleAddresses.filter((_, b) => b !== i));

  const updatePaymentSource = async (address, index) => {
    const [firstName, lastName, ...rest] = address.contactName.split(' ');
    const sourceData = {
      id: allBilling[index].paymentSourceId,
      clientSecret: allBilling[index].clientSecret,
      owner: {
        firstName,
        lastName: lastName + (rest ? ' ' + rest.join(' ') : ''),
        address: {
          line1: address.addressLine1,
          city: address.city,
          state: address.stateProvinceRegionCode,
          country: address.countryCode,
          postalCode: address.zipPostalCode,
        },
      },
    };
    const result = await digitalRiverPayment.updateSource(sourceData);
    return result;
  };

  return (
    <>
      {/* ====== Add Address Modal ⤵ ======⤵ */}
      {openAddAddressModal && (
        <CustomPopup open={openAddAddressModal} closeOnDocumentClick={false}>
          <AddAddressModal
            data-test-shipping-add-address-modal
            setCompanyName={setCompanyName}
            setContactName={setContactName}
            setAddressLine1={setAddressLine1}
            setAddressLine2={setAddressLine2}
            setCountry={setCountry}
            setCity={setCity}
            setState={setState}
            setZipPostalCode={setZipPostalCode}
            addNewAddress={addNewAddress}
            errors={errors}
            country={countryCode}
            close={closeAddAddressModal}
            setErrors={setErrors}
          />
        </CustomPopup>
      )}

      {/* ⤴ ====== ⤴ Add Address Modal ====== */}
      {/* ====== Manage Address Modal ⤵ ====== ⤵ */}
      {openManageAddressModal && (
        <CustomPopup open={openManageAddressModal} closeOnDocumentClick={false}>
          <ManageAddressModal
            addresses={values.multipleAddresses}
            close={closeManageAddressModal}
            setErrors={setErrors}
            setDefaultAddress={setDefaultAddress}
            remove={removeAddress}
            setOpenEditAddressModal={setOpenEditAddressModal}
            openEditAddressModal={openEditAddressModal}
            setClickedAddressIndex={setClickedAddressIndex}
            setOpenAddAddressModal={setOpenAddAddressModal}
            openAddAddressModal={openAddAddressModal}
            viewerAddress={state?.order?.shipTo?.address}
          />
        </CustomPopup>
      )}
      {/* ⤴ ====== ⤴ Manage Address Modal ====== */}
      <Heading
        data-test-shipping-address-form-section-title
        fontSize='1.313rem'
        paddingTop='medium'
        paddingBottom={!forBilling ? '' : 'medium'}
        letterSpacing='normal'>
        {!forBilling
          ? t('cart.shipping.shippingAddress')
          : t('cart.billing.billingAddress')}
      </Heading>
      {!forBilling && (
        <Text
          data-test-shipping-address-form-sub-title
          color='textHeading'
          paddingBottom='1rem'
          fontSize='small'
          fontStyle='italic'>
          {t('cart.shipping.shippingAvailability')}
        </Text>
      )}
      <Container>
        <Box data-test-shipping-form-sections marginBottom='medium'>
          {defaultBillingAddress.length === 1 &&
            defaultBillingAddress.map((address, i) => (
              <Flex
                key={i}
                alignItems='center'
                paddingY='medium'
                paddingBottom='small'>
                <Field name='radio' value={address}>
                  {({ field }) => (
                    <Label>
                      <Radio
                        checked={useDefault}
                        onClick={() => {
                          setUseDefault(true);
                          setCurrentPaymentIndexClicked(false);
                          useDefaultAddressForBilling(address);
                          if (values.paymentMethodRadio === 'existingSource') {
                            updatePaymentSource(address, currentIndex);
                          }
                        }}
                        name='radio'
                        marginRight={0}
                        {...field}
                        // onChange={(e: any) =>
                        //   setFieldValue('defaultBillTo', e.target.value)
                        // }
                      />
                      <Flex alignItems='center'>
                        <Address
                          address={address}
                          action={
                            <StyledText
                              onClick={() => {
                                setClickedAddressIndex(i);
                                setOpenEditAddressModal(!openEditAddressModal);
                              }}>
                              {t('cart.shipping.modal.editAddress')}
                            </StyledText>
                          }
                        />
                      </Flex>
                    </Label>
                  )}
                </Field>
              </Flex>
            ))}

          {values?.multipleAddresses?.length > 0 && <SeparatorLine />}
          {values?.multipleAddresses?.length > 0 && (
            <Heading
              letterSpacing='normal'
              paddingBottom='small'
              color='textLight3'>
              {t('cart.shipping.otherAddresses')}
            </Heading>
          )}
          {values.multipleAddresses.map(
            (address, i) =>
              !address.isDefault &&
              values.multipleAddresses.length !== 0 && (
                <Flex key={i} alignItems='center' paddingY='medium'>
                  <Field name='radio' value={address}>
                    {({ field }) => (
                      <Label>
                        <Radio
                          checked={
                            currentPaymentIndexClicked
                              ? currentIndex === i && !useDefault
                              : clickedAddressIndex === i && !useDefault
                          }
                          onClick={() => {
                            setUseDefault(false);
                            setClickedAddressIndex(i);
                            setCurrentPaymentIndexClicked(false);
                            if (
                              values.paymentMethodRadio === 'existingSource'
                            ) {
                              setShippingAddressFields(address);
                              updatePaymentSource(address, currentIndex);
                            } else {
                              setShippingOrBillingNewCard(address);
                            }
                          }}
                          name='radio'
                          marginRight={0}
                          {...field}
                          // onChange={(e: any) =>
                          //   setFieldValue(shippingOrBilling, e.target.value)
                          // }
                        />
                        <Flex alignItems='center'>
                          <Address
                            address={address}
                            action={
                              <StyledText
                                onClick={() => {
                                  setClickedAddressIndex(i);
                                  setOpenEditAddressModal(
                                    !openEditAddressModal
                                  );
                                }}>
                                {t('cart.shipping.modal.editAddress')}
                              </StyledText>
                            }
                          />
                        </Flex>
                      </Label>
                    )}
                  </Field>
                  {/* ====== Edit Address Modal ⤵ ====== ⤵ */}
                  {openEditAddressModal && clickedAddressIndex === i && (
                    <CustomPopup
                      open={openEditAddressModal}
                      closeOnDocumentClick={false}>
                      {/*
                          // FIXME: make sure this modal is using "updateAddress" correctly with respect
                          // to whether they are adding a new card or updating an existing card.
                      */}
                      <EditAddressModal
                        setFieldValue={setFieldValue}
                        address={address}
                        updateAddress={updateAddress}
                        close={closeEditAddressModal}
                        setErrors={setErrors}
                        errors={errors}
                        country={countryCode}
                        shippingOrBilling={shippingOrBilling}
                      />
                    </CustomPopup>
                  )}
                  {/* ⤴ ====== ⤴ Edit Address Modal ====== */}
                </Flex>
              )
          )}
          <Flex paddingTop={'medium'}>{/* Add section start */}</Flex>
          <Box
            alignItems={'center'}
            display={'flex'}
            justifyContent={'flex-start'}>
            <Image
              src={plusBlueIcon}
              alt='add'
              display={'flex'}
              marginRight={'xSmall'}
              onClick={() => setOpenAddAddressModal(!openAddAddressModal)}
            />
            <StyledText
              marginLeft={'medium'}
              data-test-shipping-form-add-address
              onClick={() => setOpenAddAddressModal(!openAddAddressModal)}>
              {t('cart.shipping.modal.addNewAddress')}
            </StyledText>
          </Box>
        </Box>
      </Container>
    </>
  );
};

export default BillingOrShippingAddressForm;
